import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Tooltip,
  Stack,
  Divider,
} from "@mui/material";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import Navbar from "../globals/components/Navbar";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import { apiClient } from "../globals/utils/api";
import { buildBondStatusPatchPayload } from "../globals/utils/bondStatusPatchUtils";
import { exportToExcel } from "../globals/utils/excelExport";
import EditIcon from "@mui/icons-material/Edit";
import NumberFormatCustom from "../globals/utils/Bond Creation Utils/numberFormatCustom";
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import HistoryIcon from "@mui/icons-material/History";
import PaymentsIcon from "@mui/icons-material/Payments";
import MenuItem from "@mui/material/MenuItem";
import { setArchivesFilters, setArchivesPage, resetArchivesState } from "../store/slices/bondArchivesSlice";
import TablePaginationActions from "../globals/components/TablePaginationActions";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const statusOptions = ["Active", "Approved", "Rejected", "To Be Cancelled", "Cancelled"];

/** Display label for payment status; used by table, filters, and exports. */
function bondPaymentStatusLabel(value) {
  const v = (value || "").toLowerCase();
  if (v === "paid") return "Paid";
  if (v === "unpaid") return "Unpaid";
  if (v === "na") return "N/A";
  return "Unknown";
}

const paymentStatusFilterOptions = ["Paid", "Unpaid", "Unknown", "N/A"];

const ENDORSEMENT_NAME_CHANGE_TYPES = ["Name_Change_Principal", "Name_Change_County", "Name_Change_Deceased_Ward_Beneficiary"];
const isNameChangeEndorsement = (t) => ENDORSEMENT_NAME_CHANGE_TYPES.includes(t);

const DWB_LABEL_MAP = {
  Probate: { prefix: "Deceased Legal", alias: "Alias (Deceased)" },
  Guardianship: { prefix: "Ward", alias: "Ward Alias" },
  VA: { prefix: "Beneficiary", alias: "Beneficiary Alias" },
  Trustee: { prefix: "Full Legal Name", single: true },
};

const BondArchives = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const savedFilters = useSelector((state) => state.bondArchives?.filters) || {
    bondNumber: [],
    bondType: [],
    bondSubCategory: [],
    principal: [],
    professionalGuardian: [],
    estate: [],
    lawFirm: [],
    attorney: [],
    internalUser: [],
    status: [],
    paymentStatus: [],
    startDate: null,
    endDate: null,
  };
  const savedPage = useSelector((state) => state.bondArchives?.page ?? 0);

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(savedFilters);

  const [bondTypes, setBondTypes] = useState([]);
  const [lawFirms, setLawFirms] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [professionalGuardians, setProfessionalGuardians] = useState([]);
  const [estates, setEstates] = useState([]);
  const [principals, setPrincipals] = useState([]);
  const [allBonds, setAllBonds] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery] = useState(""); // setSearchQuery removed - search UI not implemented yet
  const [page, setPage] = useState(savedPage);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedBondToCancel, setSelectedBondToCancel] = useState(null);
  const [selectedBondToLiberty, setSelectedBondToLiberty] = useState(null);
  const [cancellationFile, setCancellationFile] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBondToDelete, setSelectedBondToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMarkingCancellationComplete, setIsMarkingCancellationComplete] = useState(false);
  const [isMarkingToBeCancelled, setIsMarkingToBeCancelled] = useState(false);
  const [isMarkingLibertyComplete, setIsMarkingLibertyComplete] = useState(false);
  const [libertyConfirmDialogOpen, setLibertyConfirmDialogOpen] = useState(false);
  const [bypassHistoryDialogOpen, setBypassHistoryDialogOpen] = useState(false);
  const [bypassHistoryList, setBypassHistoryList] = useState([]);
  const [bypassHistoryLoading, setBypassHistoryLoading] = useState(false);
  const [bypassHistorySearch, setBypassHistorySearch] = useState("");
  // Payment status override state
  const [paymentOverrideDialogOpen, setPaymentOverrideDialogOpen] = useState(false);
  const [selectedBondForPayment, setSelectedBondForPayment] = useState(null);
  const [paymentOverrideStatus, setPaymentOverrideStatus] = useState("paid");
  const [paymentOverrideReason, setPaymentOverrideReason] = useState("");
  const [isSubmittingPaymentOverride, setIsSubmittingPaymentOverride] = useState(false);
  const [paymentOverrideHistoryOpen, setPaymentOverrideHistoryOpen] = useState(false);
  const [paymentOverrideHistoryList, setPaymentOverrideHistoryList] = useState([]);
  const [paymentOverrideHistoryLoading, setPaymentOverrideHistoryLoading] = useState(false);
  const [paymentOverrideHistorySearch, setPaymentOverrideHistorySearch] = useState("");

  // (Charts removed; this page only handles table and actions)

  const bondTypeToSubcategories = {
    Probate: [
      "Administrator",
      "Conservator",
      "Curator",
      "Executor",
      "Personal Representative",
    ],
    Guardianship: [
      "Guardian - Incapacitated",
      "Guardian - Minor",
      "Guardian - Advocate",
      "Conservator",
    ],
    Blanket: ["1 Year Bond", "2 Year Bond", "3 Year Bond"],
  };

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filters.lawFirm.length > 0) {
      const firmIds = filters.lawFirm.map((f) => f.value);
      Promise.all(
        firmIds.map((id) => apiClient(`/lawfirm-user-location/${id}`, "GET"))
      ).then((responses) => {
        const merged = [].concat(...responses)
          .filter((a) => a.Position === "Attorney")
          .map((a) => ({
            label: a.lawfirm_user_name,
            value: a.lawfirm_user_id,
          }));
        setAttorneys(merged);
      });
    } else {
      setAttorneys([]);
    }
  }, [filters.lawFirm]);

  const fetchInitialData = async (overrideFilters = null) => {
    setLoading(true);
    try {
      const [bt, lf, est, pr, iu, bonds, pg] = await Promise.all([
        apiClient("/bondtypes/", "GET"),
        apiClient("/list-lawfirms/", "GET"),
        apiClient("/estates", "GET"),
        apiClient("/principals", "GET"),
        apiClient("/internal-user", "GET"),
        apiClient("/archive-bonds", "GET"),
        apiClient("/get-all-professional-guardians", "GET"),
      ]);

      setBondTypes(
        bt
          .filter((b) => b.type !== "Guardian-Minor")
          .map((b) => ({ label: b.type, value: b.type }))
      );
      setLawFirms(lf.map((f) => ({ label: f.name, value: f.lawfirm_id })));
      setEstates(est.map((e, index) => ({ 
        label: e.name, 
        bondType: e.bond_type, 
        value: `${e.name}-${e.bond_type}-${index}` 
      })));
      setPrincipals(pr.map((p, index) => ({ 
        label: p.name, 
        bondType: p.role, 
        value: `${p.name}-${p.role}-${index}` 
      })));
      setInternalUsers(
        iu.data.map((u) => ({ label: u.full_name, value: u.internal_user_id }))
      );
      const formattedGuardians = (pg?.data || []).map((guardian) => ({
        label: guardian.full_name,
        value: guardian.pg_id,
      }));
      setProfessionalGuardians(formattedGuardians);

      const formatted = bonds.map((b) => ({
        bond_id: b.bond_id,
        bond_number: b.bond_number || "", // <-- NEW FIELD
        bond_type: b.bond_type,
        bond_sub_category: b.bond_sub_category,
        principals: Array.isArray(b.principals) ? b.principals : [],
        estate_name: b.name,
        law_firm_name: b.law_firm_name,
        attorney_name: b.law_firm_user_name,
        underwriting_status: b.underwriting_status || "-",
        bond_amount: b.bond_amount || "0.00",
        managed_by: Array.isArray(b.managed_by) ? b.managed_by.map((u) => u.full_name) : [],
        status: b.status || "Active",
        created_at: b.created_at,
        payment_status: b.payment_status ?? null,
        payment_status_source: b.payment_status_source ?? null,
        payment_status_updated_at: b.payment_status_updated_at ?? null,
      }));

      setAllBonds(formatted);
      // Re-apply filters; allow override to avoid using stale state on manual refresh
      let result = [...formatted];
      const f = overrideFilters ?? filters ?? savedFilters;

      if (f.bondNumber?.length)
        result = result.filter((b) => f.bondNumber.some((x) => b.bond_number === x.value));

      if (f.startDate) {
        const start = dayjs(f.startDate).startOf("day");
        result = result.filter((b) => dayjs(b.created_at).isSameOrAfter(start, "day"));
      }

      if (f.endDate) {
        const end = dayjs(f.endDate).endOf("day");
        result = result.filter((b) => dayjs(b.created_at).isSameOrBefore(end, "day"));
      }

      if (f.bondType?.length)
        result = result.filter((b) => f.bondType.some((x) => x.value === b.bond_type));

      if (f.bondSubCategory?.length)
        result = result.filter((b) => f.bondSubCategory.includes(b.bond_sub_category));

      // If Professional Guardian filter is applied, narrow results to guardian's bonds via IDs
      if (f.professionalGuardian?.length) {
        const guardianIds = f.professionalGuardian.map((g) => g.value);
        const guardianBondsArrays = await Promise.all(
          guardianIds.map((gid) => apiClient(`/list-guardian-bonds/${gid}/`, "GET"))
        );
        const allowedIds = new Set(
          guardianBondsArrays.flat().map((gb) => gb.bond_id)
        );
        result = result.filter((b) => allowedIds.has(b.bond_id));
      }

      // Apply Principal filter by matching principal names
      if (f.principal?.length) {
        result = result.filter((b) => {
          const principals = Array.isArray(b.principals)
            ? b.principals
            : (b.principals || "").split(",").map((p) => p.trim());
          return f.principal.some((x) =>
            principals.some((p) => p.toLowerCase() === x.label.toLowerCase())
          );
        });
      }

      if (f.estate?.length)
        result = result.filter((b) => f.estate.some((x) => b.estate_name === x.label));

      if (f.lawFirm?.length)
        result = result.filter((b) => f.lawFirm.some((x) => b.law_firm_name === x.label));

      if (f.attorney?.length)
        result = result.filter((b) => f.attorney.some((x) => b.attorney_name === x.label));

      if (f.internalUser?.length)
        result = result.filter((b) => f.internalUser.some((x) => Array.isArray(b.managed_by) && b.managed_by.includes(x.label)));

      if (f.status?.length)
        result = result.filter((b) => f.status.includes(b.status));

      if (f.paymentStatus?.length)
        result = result.filter((b) =>
          f.paymentStatus.includes(bondPaymentStatusLabel(b.payment_status))
        );

      setFilteredData(result);
    } catch (err) {
      console.error("Error loading data", err);
    } finally {
      setLoading(false);
    }
  };

  const sortRows = (rows) => {
    return [...rows].sort((a, b) => {
      if (orderBy === "bond_amount") {
        const valA = parseFloat(a.bond_amount);
        const valB = parseFloat(b.bond_amount);
        return order === "asc" ? valA - valB : valB - valA;
      } else if (orderBy === "created_at") {
        return order === "asc"
          ? new Date(a.created_at) - new Date(b.created_at)
          : new Date(b.created_at) - new Date(a.created_at);
      } else {
        const valA = a[orderBy]?.toString().toLowerCase() || "";
        const valB = b[orderBy]?.toString().toLowerCase() || "";
        return order === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
    });
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  const handleExport = () => {
    const dataToExport = filteredData.map((b) => ({
      "Bond Number": b.bond_number,
      "Bond Type": b.bond_type,
      "Bond Sub Category": b.bond_sub_category,
      Principals: Array.isArray(b.principals) ? b.principals.join(", ") : (b.principals || ""),
      "Estate Name": b.estate_name,
      "Law Firm": b.law_firm_name,
      Attorney: b.attorney_name,
      "Managed By": Array.isArray(b.managed_by) ? b.managed_by.join(", ") : (b.managed_by || ""),
      "Bond Amount": b.bond_amount,
      Status: b.status,
      "Created At": dayjs(b.created_at).format("DD MMM YYYY"),
    }));
    exportToExcel(dataToExport, "Bonds", "bond_archives_export");
  };

  const applyFilters = () => {
    let result = [...allBonds];

    if (filters.bondNumber.length)
      result = result.filter((b) =>
        filters.bondNumber.some((f) => b.bond_number === f.value)
      );

    if (filters.startDate) {
      const start = dayjs(filters.startDate).startOf("day");
      result = result.filter((b) =>
        dayjs(b.created_at).isSameOrAfter(start, "day")
      );
    }

    if (filters.endDate) {
      const end = dayjs(filters.endDate).endOf("day");
      result = result.filter((b) =>
        dayjs(b.created_at).isSameOrBefore(end, "day")
      );
    }

    if (filters.bondType.length)
      result = result.filter((b) =>
        filters.bondType.some((f) => f.value === b.bond_type)
      );

    if (filters.bondSubCategory.length)
      result = result.filter((b) =>
        filters.bondSubCategory.includes(b.bond_sub_category)
      );

    // If Professional Guardian is selected, restrict to guardian's bond IDs
    if (filters.professionalGuardian.length) {
      // Note: applyFilters isn't async; use optimistic last-loaded guardian IDs by refetching synchronously at initial filter application time
      // Defer to server by calling synchronously here via async IIFE
      (async () => {
        const guardianIds = filters.professionalGuardian.map((g) => g.value);
        const guardianBondsArrays = await Promise.all(
          guardianIds.map((gid) => apiClient(`/list-guardian-bonds/${gid}/`, "GET"))
        );
        const allowedIds = new Set(
          guardianBondsArrays.flat().map((gb) => gb.bond_id)
        );
        const next = result.filter((b) => allowedIds.has(b.bond_id));
        // After narrowing by guardian, continue principal match if any
        let final = next;
        if (filters.principal.length) {
          final = final.filter((b) => {
            const principals = Array.isArray(b.principals)
              ? b.principals
              : (b.principals || "").split(",").map((p) => p.trim());
            return filters.principal.some((x) =>
              principals.some((p) => p.toLowerCase() === x.label.toLowerCase())
            );
          });
        }
        if (filters.paymentStatus?.length) {
          final = final.filter((b) =>
            filters.paymentStatus.includes(bondPaymentStatusLabel(b.payment_status))
          );
        }
        setFilteredData(final);
        setFilterDialogOpen(false);
        setPage(0);
        dispatch(setArchivesFilters(filters));
        dispatch(setArchivesPage(0));
      })();
      return; // exit; async IIFE will finish updates
    }

    // Otherwise, only apply principal matching
    if (filters.principal.length) {
      result = result.filter((b) => {
        const principals = Array.isArray(b.principals)
          ? b.principals
          : (b.principals || "").split(",").map((p) => p.trim());
        return filters.principal.some((x) =>
          principals.some((p) => p.toLowerCase() === x.label.toLowerCase())
        );
      });
    }

    if (filters.estate.length)
      result = result.filter((b) =>
        filters.estate.some((f) => b.estate_name === f.label)
      );

    if (filters.lawFirm.length)
      result = result.filter((b) =>
        filters.lawFirm.some((f) => b.law_firm_name === f.label)
      );

    if (filters.attorney.length)
      result = result.filter((b) =>
        filters.attorney.some((f) => b.attorney_name === f.label)
      );

    if (filters.internalUser.length)
      result = result.filter((b) =>
        filters.internalUser.some((f) => Array.isArray(b.managed_by) && b.managed_by.includes(f.label))
      );

    if (filters.status.length)
      result = result.filter((b) => filters.status.includes(b.status));

    if (filters.paymentStatus?.length)
      result = result.filter((b) =>
        filters.paymentStatus.includes(bondPaymentStatusLabel(b.payment_status))
      );

    setFilteredData(result);
    setFilterDialogOpen(false);
    setPage(0);
    dispatch(setArchivesFilters(filters));
    dispatch(setArchivesPage(0));
  };

  const clearFilters = () => {
    const cleared = {
      bondNumber: [],
      bondType: [],
      bondSubCategory: [],
      principal: [],
      professionalGuardian: [],
      estate: [],
      lawFirm: [],
      attorney: [],
      internalUser: [],
      status: [],
      paymentStatus: [],
      startDate: null,
      endDate: null,
    };
    setFilters(cleared);
    dispatch(setArchivesFilters(cleared));
    setPage(0);
    dispatch(setArchivesPage(0));
  };

  const selectedBondTypes = filters.bondType.map((b) => b.value);

  const availableSubcategories = selectedBondTypes.flatMap(
    (type) => bondTypeToSubcategories[type] || []
  );

  const availablePrincipals = selectedBondTypes.length
    ? principals.filter((p) => selectedBondTypes.includes(p.bondType))
    : principals;

  const availableEstates = selectedBondTypes.length
    ? estates.filter((e) => selectedBondTypes.includes(e.bondType))
    : estates;

  const filteredRows = sortRows(
    filteredData.filter((r) => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        (Array.isArray(r.principals) && r.principals.some((p) => p.toLowerCase().includes(query))) ||
        r.bond_number?.toLowerCase().includes(query) ||
        r.estate_name?.toLowerCase().includes(query) ||
        r.law_firm_name?.toLowerCase().includes(query) ||
        r.attorney_name?.toLowerCase().includes(query) ||
        (Array.isArray(r.managed_by) && r.managed_by.some((m) => m.toLowerCase().includes(query))) ||
        r.status?.toLowerCase().includes(query)
      );
    })
  ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getCancellationInstructionsList = (bond) => {
    const amount = parseFloat(bond.bond_amount || 0);
    const type = bond.bond_type;
    if (type === "Probate") {
      if (amount <= 250000) {
        return [
          "Discharge from the court",
          "Statement from attorney on their letterhead stating all debts are paid and balances distributed to the remaining heirs",
        ];
      }
      return [
        "Final Accounting",
        "Discharge from the court",
        "Copy of verification of closing evidence from a court's online system",
      ];
    }
    if (type === "Guardianship") {
      const main =
        amount <= 1000000
          ? [
              "Final Accounting",
              "Discharge from the court",
              "Statement from attorney on their letterhead stating all debts are paid and balances distributed to the remaining heirs and/or beneficiaries",
            ]
          : [
              "Final Accounting",
              "Discharge from the court",
              "Copy of verification of closing evidence from a court's online system",
            ];
      return [
        ...main,
        "If a Successor Bond has been filed with the court:",
        "Guardianship - Evidence of death of the incompetent",
        "Minor Guardian - Evidence of the minor reaching the age of majority",
      ];
    }
    if (type === "VA") return ["Exoneration Letter from the VA"];
    if (type === "Trustee") {
      return [
        "Court's Release",
        "Final Accounting",
        "Evidence that either the beneficiary of trust has died or that trust assets have been fully dispersed to beneficiary(s) per the Trust",
      ];
    }
    if (type === "Blanket") return ["Email confirmation that renewal is not required."];
    return ["Upload appropriate closing document for this bond."];
  };

  const [endorsementDialogOpen, setEndorsementDialogOpen] = useState(false);
  const [selectedBondToEndorse, setSelectedBondToEndorse] = useState(null);
  const [endorsementType, setEndorsementType] = useState("Increase");
  const [newBondAmount, setNewBondAmount] = useState("");
  const [endorsementFile, setEndorsementFile] = useState(null);
  const [isSubmittingEndorsement, setIsSubmittingEndorsement] = useState(false);
  const userdetails = useSelector((state) => state.auth.user);
  const role = useSelector((state) => state.auth.role);
  const canOverridePaymentStatus =
    role === "internal_user" || role === "super_user";
  const [contactName, setContactName] = useState(userdetails.first_name + " " + userdetails.last_name);
  const [contactEmail, setContactEmail] = useState(userdetails.email);
  const [updatedName, setUpdatedName] = useState("");
  const [counties, setCounties] = useState([]);
  const [endorsementToCounty, setEndorsementToCounty] = useState("");
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [estimatedAssets, setEstimatedAssets] = useState("");
  const [dwbFirstName, setDwbFirstName] = useState("");
  const [dwbMiddleName, setDwbMiddleName] = useState("");
  const [dwbLastName, setDwbLastName] = useState("");
  const [dwbSuffix, setDwbSuffix] = useState("");
  const [dwbAlias, setDwbAlias] = useState("");

  const getEndorsementInstructions = (bond) => {
    const type = bond.bond_type;

    const typeSupports = {
      courtOrder: ["Probate", "Guardianship", "Trustee"],
      accounting: ["Guardianship", "Trustee"],
      vaLetter: ["VA"],
    };

    return [
      ...(typeSupports.courtOrder.includes(type)
        ? ["• Court Order or Endorsement Checklist"]
        : []),
      ...(typeSupports.accounting.includes(type)
        ? ["• Annual Accounting Documentation"]
        : []),
      ...(typeSupports.vaLetter.includes(type)
        ? ["• VA Letter of Endorsement"]
        : []),
    ];
  };

  const bondStateId = selectedBondToEndorse?.state ?? 6;
  useEffect(() => {
    if (!endorsementDialogOpen || !bondStateId) return;
    const fetchCounties = async () => {
      setLoadingCounties(true);
      try {
        const countyResponse = await apiClient(
          `/counties-by-state/${bondStateId}/`,
          "GET"
        );
        const formatted = (countyResponse || []).map((c) => ({
          label: c.bond_county,
          value: c.bond_county,
        }));
        setCounties(formatted);
      } catch {
        setCounties([]);
      } finally {
        setLoadingCounties(false);
      }
    };
    fetchCounties();
  }, [endorsementDialogOpen, bondStateId]);

  const tableColumns = [
    "bond_number",
    "bond_type",
    "bond_sub_category",
    "principals",
    "estate_name",
    "law_firm_name",
    "attorney_name",
    "managed_by",
    "bond_amount",
    "status",
    "payment_status",
    "created_at",
  ];

  const formatPaymentStatus = (value) => bondPaymentStatusLabel(value);

  const getPaymentStatusColor = (value) => {
    const v = (value || "").toLowerCase();
    if (v === "paid") return "#28a745";
    if (v === "unpaid") return "#dc3545";
    if (v === "na") return "#6c757d";
    return "#999";
  };

  const handleDeleteClick = (bond) => {
    setSelectedBondToDelete(bond);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setSelectedBondToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBondToDelete) return;

    setIsDeleting(true);
    try {
      const bondType = selectedBondToDelete.bond_type.toLowerCase();
      let endpoint = '';
      
      switch (bondType) {
        case 'blanket':
          endpoint = `/delete-blanket-bond/${selectedBondToDelete.bond_id}/`;
          break;
        case 'guardianship':
          endpoint = `/delete_guardianship_bond/${selectedBondToDelete.bond_id}/`;
          break;
        case 'probate':
          endpoint = `/delete_probate_bond/${selectedBondToDelete.bond_id}/`;
          break;
        case 'trustee':
          endpoint = `/delete_trustee_bond/${selectedBondToDelete.bond_id}/`;
          break;
        case 'va':
          endpoint = `/delete_va_bond/${selectedBondToDelete.bond_id}/`;
          break;
        default:
          throw new Error('Invalid bond type');
      }

      await apiClient(endpoint, "DELETE");
      toast.success("Bond deleted successfully");
      handleDeleteClose();
      await fetchInitialData();
    } catch (error) {
      console.error("Error deleting bond:", error);
      toast.error("Failed to delete bond");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMarkCancellationComplete = async () => {
    if (!selectedBondToCancel) return;

    setIsMarkingCancellationComplete(true);
    try {
      const bondType = selectedBondToCancel.bond_type.toLowerCase();
      let endpoint = '';
      
      switch (bondType) {
        case 'blanket':
          endpoint = `/update-blanket-bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'guardianship':
          endpoint = `/update_guardianship_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'probate':
          endpoint = `/update_probate_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'trustee':
          endpoint = `/update_trustee_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'va':
          endpoint = `/update_va_bond/${selectedBondToCancel.bond_id}/`;
          break;
        default:
          throw new Error('Invalid bond type');
      }

      const payload = buildBondStatusPatchPayload(bondType, "Cancelled");

      await apiClient(endpoint, "PATCH", payload);
      toast.success("Bond cancellation marked as completed successfully");
      setCancelDialogOpen(false);
      await fetchInitialData();
    } catch (error) {
      console.error("Error marking cancellation as complete:", error);
      toast.error("Failed to mark cancellation as completed");
    } finally {
      setIsMarkingCancellationComplete(false);
    }
  };

  const handleRevertCancellation = async () => {
    if (!selectedBondToCancel) return;

    setIsMarkingCancellationComplete(true);
    try {
      const bondType = selectedBondToCancel.bond_type.toLowerCase();
      let endpoint = '';
      switch (bondType) {
        case 'blanket':
          endpoint = `/update-blanket-bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'guardianship':
          endpoint = `/update_guardianship_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'probate':
          endpoint = `/update_probate_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'trustee':
          endpoint = `/update_trustee_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'va':
          endpoint = `/update_va_bond/${selectedBondToCancel.bond_id}/`;
          break;
        default:
          throw new Error('Invalid bond type');
      }

      const payload = buildBondStatusPatchPayload(bondType, "Approved");

      await apiClient(endpoint, "PATCH", payload);
      toast.success("Bond cancellation reverted successfully");
      setCancelDialogOpen(false);
      await fetchInitialData();
    } catch (error) {
      console.error("Error reverting cancellation:", error);
      toast.error("Failed to revert cancellation");
    } finally {
      setIsMarkingCancellationComplete(false);
    }
  };

  const handleMarkToBeCancelled = async () => {
    if (!selectedBondToCancel) return;

    setIsMarkingToBeCancelled(true);
    try {
      const bondType = selectedBondToCancel.bond_type.toLowerCase();
      let endpoint = '';
      switch (bondType) {
        case 'blanket':
          endpoint = `/update-blanket-bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'guardianship':
          endpoint = `/update_guardianship_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'probate':
          endpoint = `/update_probate_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'trustee':
          endpoint = `/update_trustee_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'va':
          endpoint = `/update_va_bond/${selectedBondToCancel.bond_id}/`;
          break;
        default:
          throw new Error('Invalid bond type');
      }

      const payload = buildBondStatusPatchPayload(bondType, "To Be Cancelled");

      await apiClient(endpoint, "PATCH", payload);
      toast.success("Bond marked as 'To Be Cancelled'");
      setCancelDialogOpen(false);
      await fetchInitialData();
    } catch (error) {
      console.error("Error marking to be cancelled:", error);
      toast.error("Failed to mark bond 'To Be Cancelled'");
    } finally {
      setIsMarkingToBeCancelled(false);
    }
  };

  const handleLibertyBypassConfirm = async () => {
    if (!selectedBondToLiberty) return;
    const bypassedBy = (userdetails?.first_name && userdetails?.last_name)
      ? `${userdetails.first_name} ${userdetails.last_name}`
      : (userdetails?.email || "Unknown");
    setIsMarkingLibertyComplete(true);
    try {
      await apiClient("/liberty-bypass/", "POST", {
        bond_id: selectedBondToLiberty.bond_id,
        bypassed_by: bypassedBy,
      });
      toast.success("Bond marked as Liberty complete. Notification sent.");
      setLibertyConfirmDialogOpen(false);
      setSelectedBondToLiberty(null);
      await fetchInitialData();
    } catch (error) {
      console.error("Liberty bypass error:", error);
      toast.error(error?.response?.data?.detail || error?.message || "Failed to mark Liberty complete");
    } finally {
      setIsMarkingLibertyComplete(false);
    }
  };

  const fetchBypassHistory = async () => {
    setBypassHistoryLoading(true);
    try {
      const data = await apiClient("/liberty-bypass/list/", "GET");
      setBypassHistoryList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch bypass history:", error);
      toast.error("Failed to load bypass history");
      setBypassHistoryList([]);
    } finally {
      setBypassHistoryLoading(false);
    }
  };

  const handleOpenBypassHistory = () => {
    setBypassHistorySearch("");
    setBypassHistoryDialogOpen(true);
    fetchBypassHistory();
  };

  const handleOpenPaymentOverride = (bond) => {
    setSelectedBondForPayment(bond);
    setPaymentOverrideStatus(
      bond.payment_status && ["paid", "unpaid", "na"].includes(String(bond.payment_status).toLowerCase())
        ? String(bond.payment_status).toLowerCase()
        : "paid"
    );
    setPaymentOverrideReason("");
    setPaymentOverrideDialogOpen(true);
  };

  const handlePaymentOverrideConfirm = async () => {
    if (!selectedBondForPayment) return;
    const overriddenBy = (userdetails?.first_name && userdetails?.last_name)
      ? `${userdetails.first_name} ${userdetails.last_name}`
      : (userdetails?.email || "Unknown");
    setIsSubmittingPaymentOverride(true);
    try {
      await apiClient("/liberty-payment-status/override/", "POST", {
        bond_id: selectedBondForPayment.bond_id,
        new_status: paymentOverrideStatus,
        reason: paymentOverrideReason,
        overridden_by: overriddenBy,
      });
      toast.success("Payment status updated. Notification sent.");
      setPaymentOverrideDialogOpen(false);
      setSelectedBondForPayment(null);
      await fetchInitialData();
    } catch (error) {
      console.error("Payment override error:", error);
      toast.error(error?.response?.data?.detail || error?.message || "Failed to update payment status");
    } finally {
      setIsSubmittingPaymentOverride(false);
    }
  };

  const fetchPaymentOverrideHistory = async () => {
    setPaymentOverrideHistoryLoading(true);
    try {
      const data = await apiClient("/liberty-payment-status/override/list/", "GET");
      setPaymentOverrideHistoryList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch payment override history:", error);
      toast.error("Failed to load payment override history");
      setPaymentOverrideHistoryList([]);
    } finally {
      setPaymentOverrideHistoryLoading(false);
    }
  };

  const handleOpenPaymentOverrideHistory = () => {
    setPaymentOverrideHistorySearch("");
    setPaymentOverrideHistoryOpen(true);
    fetchPaymentOverrideHistory();
  };

  const handleReviewBond = () => {
    if (!selectedBondToDelete) return;
    const type = selectedBondToDelete.bond_type.toLowerCase().replace(" ", "-");
    window.open(`/vermost-${type}-bond-update/${selectedBondToDelete.bond_id}`, "_blank");
  };

  return (
    <>
      <Navbar />
      <Box sx={{ padding: "20px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", maxWidth: "60vw" }}>
          <Typography variant="h4" fontWeight="bold">
            Bond Archives
          </Typography>
          <span style={{ fontSize: "14px", color: "#666" }}>Here are all the bonds that have been processed through the system to date.</span>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mb: 2 }}>
            <Button onClick={handleOpenBypassHistory} startIcon={<HistoryIcon />}>
              View API Bypass History
            </Button>
            {canOverridePaymentStatus ? (
              <Button onClick={handleOpenPaymentOverrideHistory} startIcon={<HistoryIcon />}>
                Payment Override History
              </Button>
            ) : null}
            <Button onClick={handleExport} startIcon={<DownloadIcon />}>
              Export
            </Button>
            <Button
              onClick={() => {
                // Manual refresh should reset filters and page
    const cleared = {
                  bondNumber: [],
                  bondType: [],
                  bondSubCategory: [],
                  principal: [],
      professionalGuardian: [],
                  estate: [],
                  lawFirm: [],
                  attorney: [],
                  internalUser: [],
                  status: [],
                  paymentStatus: [],
                  startDate: null,
                  endDate: null,
                };
                setFilters(cleared);
                setPage(0);
                dispatch(resetArchivesState());
                fetchInitialData(cleared);
              }}
              startIcon={<RefreshIcon />}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
            <Button
              variant="contained"
              onClick={() => setFilterDialogOpen(true)}
              startIcon={<FilterListIcon />}
            >
              Filters
            </Button>
          </Box>

        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                {tableColumns.map((col, i) => (
                  <TableCell
                    key={i}
                    sortDirection={orderBy === col ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === col}
                      direction={orderBy === col ? order : "asc"}
                      onClick={() => handleSort(col)}
                    >
                      {col
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ '& tr:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    No Data Available
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((bond, index) => (
                  <TableRow key={index}>
                    <TableCell>{bond.bond_number}</TableCell>
                    <TableCell>{bond.bond_type}</TableCell>
                    <TableCell>{bond.bond_sub_category}</TableCell>
                    <TableCell>{Array.isArray(bond.principals) ? bond.principals.join(", ") : (bond.principals || "N/A")}</TableCell>
                    <TableCell>{bond.estate_name}</TableCell>
                    <TableCell>{bond.law_firm_name}</TableCell>
                    <TableCell>{bond.attorney_name}</TableCell>
                    <TableCell>{Array.isArray(bond.managed_by) ? bond.managed_by.join(", ") : (bond.managed_by || "N/A")}</TableCell>
                    <TableCell>
                      ${Number(bond.bond_amount).toLocaleString()}
                    </TableCell>
                    <TableCell>{bond.status}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          px: 1.2,
                          py: 0.3,
                          borderRadius: 1,
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#fff",
                          backgroundColor: getPaymentStatusColor(bond.payment_status),
                        }}
                        title={
                          bond.payment_status_source === "manual_override"
                            ? "Manually overridden"
                            : bond.payment_status_updated_at
                              ? `Updated ${dayjs(bond.payment_status_updated_at).format("DD MMM YYYY h:mm A")}`
                              : "Not yet checked"
                        }
                      >
                        {formatPaymentStatus(bond.payment_status)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {dayjs(bond.created_at).format("DD MMM YYYY")}
                    </TableCell>
                    <TableCell align="center">
                      {canOverridePaymentStatus ? (
                        <Tooltip title="Override Payment Status">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenPaymentOverride(bond)}
                            disabled={isSubmittingPaymentOverride}
                          >
                            <PaymentsIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="View Bond">
                        <IconButton
                          onClick={() => {
                            const type = bond.bond_type
                              .toLowerCase()
                              .replace(" ", "-");
                            window.open(
                              `/vermost-${type}-bond-update/${bond.bond_id}`,
                              "_blank"
                            );
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mark Liberty Complete">
                        <IconButton
                          color="success"
                          onClick={() => {
                            setSelectedBondToLiberty(bond);
                            setLibertyConfirmDialogOpen(true);
                          }}
                          disabled={isMarkingLibertyComplete}
                        >
                          <DoneIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel Bond">
                        <IconButton
                          color="error"
                          onClick={() => {
                            setSelectedBondToCancel(bond);
                            setCancellationFile(null);
                            setCancelDialogOpen(true);
                          }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                      {/* Endorsement (Edit) */}
                      <Tooltip title="Bond Endorsement">
                        <IconButton
                          color="secondary"
                          onClick={() => {
                            setSelectedBondToEndorse(bond);
                            setEndorsementType("Increase");
                            setNewBondAmount("");
                            setEndorsementFile(null);
                            setUpdatedName("");
                            setEndorsementToCounty("");
                            setEstimatedAssets("");
                            setDwbFirstName("");
                            setDwbMiddleName("");
                            setDwbLastName("");
                            setDwbSuffix("");
                            setDwbAlias("");
                            setEndorsementDialogOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Bond">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(bond)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={(e, p) => { setPage(p); dispatch(setArchivesPage(p)); }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value))}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{ mt: 2 }}
        />

      <Dialog
        open={cancelDialogOpen}
        onClose={() => !isCancelling && !isMarkingCancellationComplete && setCancelDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 2.5,
            fontWeight: 700,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Bond Cancellation
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => !isCancelling && !isMarkingCancellationComplete && setCancelDialogOpen(false)}
            edge="end"
            size="small"
            disabled={isCancelling || isMarkingCancellationComplete}
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          {selectedBondToCancel && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                Cancellation Details
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <strong>Bond Number:</strong> {selectedBondToCancel.bond_number ?? "N/A"}
              </Typography>
              <Typography sx={{ mb: 2 }}>
                <strong>Bond Type:</strong> {selectedBondToCancel.bond_type}
              </Typography>

              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                Documents
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                Acceptable Documentation:
              </Typography>
              <ul style={{ paddingLeft: "24px", marginBottom: "20px", listStyleType: "disc" }}>
                {getCancellationInstructionsList(selectedBondToCancel).map(
                  (item, idx) => (
                    <li
                      key={idx}
                      style={{ fontSize: "0.9rem", marginBottom: "6px" }}
                    >
                      {item}
                    </li>
                  )
                )}
              </ul>

              <Button
                variant="outlined"
                component="label"
                disabled={isCancelling || isMarkingCancellationComplete}
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2 }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    const validTypes = [
                      "application/pdf",
                      "application/msword",
                      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    ];
                    if (file && !validTypes.includes(file.type)) {
                      toast.error("Only PDF or Word documents are allowed.");
                      e.target.value = "";
                      return;
                    }
                    setCancellationFile(file);
                    toast.success("File selected successfully!");
                  }}
                />
              </Button>
              {cancellationFile && (
                <Typography variant="caption" sx={{ display: "block", mb: 2 }}>
                  Selected File: {cancellationFile.name}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: "100%" }}
          >
            {selectedBondToCancel && (
              <Stack direction="row" spacing={1.25}>
                <Button
                  onClick={handleMarkToBeCancelled}
                  color="warning"
                  variant="outlined"
                  size="small"
                  disabled={isCancelling || isMarkingCancellationComplete || isMarkingToBeCancelled}
                  startIcon={isMarkingToBeCancelled && <CircularProgress size={14} />}
                >
                  {isMarkingToBeCancelled ? "Marking..." : "Mark To Be Cancelled"}
                </Button>
                <Button
                  onClick={handleMarkCancellationComplete}
                  color="error"
                  variant="outlined"
                  size="small"
                  disabled={isCancelling || isMarkingCancellationComplete}
                  startIcon={isMarkingCancellationComplete && <CircularProgress size={14} />}
                >
                  {isMarkingCancellationComplete ? "Marking Cancelled..." : "Mark as Cancelled"}
                </Button>

                <Button
                  onClick={handleRevertCancellation}
                  color="primary"
                  variant="outlined"
                  size="small"
                  disabled={isCancelling || isMarkingCancellationComplete}
                  startIcon={isMarkingCancellationComplete && <CircularProgress size={14} />}
                >
                  {isMarkingCancellationComplete ? "Reverting..." : "Revert Cancellation"}
                </Button>
              </Stack>
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => setCancelDialogOpen(false)} disabled={isCancelling || isMarkingCancellationComplete}>
                Close
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={!cancellationFile || isCancelling || isMarkingCancellationComplete}
                onClick={async () => {
                  if (!cancellationFile || !selectedBondToCancel) return;
                  setIsCancelling(true);
                  const formData = new FormData();
                  formData.append("cancellation_file", cancellationFile);
                  try {
                    await apiClient(
                      `/cancel-bond/`,
                      "POST",
                      formData,
                      { bond_id: selectedBondToCancel.bond_id },
                      { "Content-Type": "multipart/form-data" }
                    );
                    toast.success("Bond cancelled successfully!");
                    setCancelDialogOpen(false);
                    await fetchInitialData();
                  } catch (error) {
                    console.error(error);
                    toast.error(error.message || "Failed to cancel the bond.");
                  } finally {
                    setIsCancelling(false);
                  }
                }}
              >
                {isCancelling ? <CircularProgress size={22} color="inherit" /> : "Submit Cancellation"}
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>

        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Apply Filters</DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <Autocomplete
              multiple
              options={bondTypes}
              value={filters.bondType}
              onChange={(e, v) => {
                const next = { ...filters, bondType: v };
                setFilters(next);
                dispatch(setArchivesFilters(next));
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Bond Type" />}
            />
            <Autocomplete
              multiple
              options={availableSubcategories}
              value={filters.bondSubCategory}
              onChange={(e, v) => {
                const next = { ...filters, bondSubCategory: v };
                setFilters(next);
                dispatch(setArchivesFilters(next));
              }}
              renderInput={(p) => (
                <TextField {...p} label="Bond Sub Category" />
              )}
            />
            <Autocomplete
              multiple
              options={[
                ...new Set(allBonds.map((b) => b.bond_number).filter(Boolean)),
              ].map((num) => ({ label: num, value: num }))}
              value={filters.bondNumber}
              onChange={(e, v) => {
                const next = { ...filters, bondNumber: v };
                setFilters(next);
                dispatch(setArchivesFilters(next));
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Bond Number" />}
            />

            <Autocomplete
              multiple
              options={availablePrincipals}
              value={filters.principal}
              onChange={(e, v) => {
                const next = { ...filters, principal: v };
                setFilters(next);
                dispatch(setArchivesFilters(next));
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Principal" />}
            />
            <Autocomplete
              multiple
              options={availableEstates}
              value={filters.estate}
              onChange={(e, v) => {
                const next = { ...filters, estate: v };
                setFilters(next);
                dispatch(setArchivesFilters(next));
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Estate / Ward / Trust / Beneficiary" />}
            />
            <Autocomplete
              multiple
              options={lawFirms}
              value={filters.lawFirm}
              onChange={(e, v) => {
                const next = { ...filters, lawFirm: v };
                setFilters(next);
                dispatch(setArchivesFilters(next));
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Law Firm" />}
            />
            <Autocomplete
              multiple
              options={attorneys}
              value={filters.attorney}
              onChange={(e, v) => {
                const next = { ...filters, attorney: v };
                setFilters(next);
                dispatch(setArchivesFilters(next));
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Attorney" />}
            />
            <Autocomplete
              multiple
              options={professionalGuardians}
              value={filters.professionalGuardian || []}
              onChange={(e, v) => {
                const next = { ...filters, professionalGuardian: v };
                setFilters(next);
                dispatch(setArchivesFilters(next));
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Professional Guardian" />}
            />
            <Autocomplete
              multiple
              options={internalUsers}
              value={filters.internalUser}
              onChange={(e, v) => {
                const next = { ...filters, internalUser: v };
                setFilters(next);
                dispatch(setArchivesFilters(next));
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Managed By" />}
            />
            <Autocomplete
              multiple
              options={statusOptions}
              value={filters.status}
              onChange={(e, v) => {
                const next = { ...filters, status: v };
                setFilters(next);
                dispatch(setArchivesFilters(next));
              }}
              renderInput={(p) => <TextField {...p} label="Status" />}
            />
            <Autocomplete
              multiple
              options={paymentStatusFilterOptions}
              value={filters.paymentStatus || []}
              onChange={(e, v) => {
                const next = { ...filters, paymentStatus: v };
                setFilters(next);
                dispatch(setArchivesFilters(next));
              }}
              renderInput={(p) => <TextField {...p} label="Payment Status" />}
            />
            <TextField
              type="date"
              label="Start Date"
              value={filters.startDate || ""}
              onChange={(e) => {
                const next = { ...filters, startDate: e.target.value };
                setFilters(next);
                dispatch(setArchivesFilters(next));
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="End Date"
              value={filters.endDate || ""}
              onChange={(e) => {
                const next = { ...filters, endDate: e.target.value };
                setFilters(next);
                dispatch(setArchivesFilters(next));
              }}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={clearFilters}>Clear</Button>
            <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={applyFilters}>
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {/* Endorsement Dialog */}
        <Dialog
          open={endorsementDialogOpen}
          onClose={() =>
            !isSubmittingEndorsement && setEndorsementDialogOpen(false)
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1.3rem' }}>Bond Endorsement</DialogTitle>
          <DialogContent dividers>
            {selectedBondToEndorse?.bond_type === "Blanket" ? (
              <Typography variant="h6" sx={{ py: 5, textAlign: "center" }}>
                Endorsement not allowed for Blanket.
              </Typography>
            ) : selectedBondToEndorse ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Endorsement Details
                </Typography>
                <Typography sx={{ mb: 1 }}>
                  <strong>Bond Number:</strong> {selectedBondToEndorse.bond_number ?? "N/A"}
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  <strong>Bond Type:</strong> {selectedBondToEndorse.bond_type}
                </Typography>

                <TextField
                  label="Endorsement Type"
                  select
                  fullWidth
                  value={endorsementType}
                  onChange={(e) => setEndorsementType(e.target.value)}
                  SelectProps={{ native: true }}
                  required
                  sx={{ mb: 2 }}
                >
                  <option value="Increase">Increase</option>
                  <option value="Decrease">Decrease</option>
                  <option value="Name_Change_Principal">Name change (Principal)</option>
                  <option value="Name_Change_County">Name change (County)</option>
                  <option value="Name_Change_Deceased_Ward_Beneficiary">Name change (Deceased/Ward/Beneficiary)</option>
                </TextField>

                {isNameChangeEndorsement(endorsementType) ? (
                  endorsementType === "Name_Change_County" ? (
                    <Autocomplete
                      options={counties}
                      loading={loadingCounties}
                      value={
                        endorsementToCounty
                          ? counties.find((c) => c.value === endorsementToCounty) || null
                          : null
                      }
                      onChange={(e, selected) =>
                        setEndorsementToCounty(selected ? selected.value : "")
                      }
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.label
                      }
                      isOptionEqualToValue={(option, value) =>
                        option && value && option.value === value.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="To County"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingCounties ? (
                                  <CircularProgress color="inherit" size={18} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      fullWidth
                      disabled={loadingCounties}
                      sx={{ mb: 3 }}
                    />
                  ) : endorsementType === "Name_Change_Deceased_Ward_Beneficiary" ? (
                  (() => {
                    const labels = DWB_LABEL_MAP[selectedBondToEndorse?.bond_type] || DWB_LABEL_MAP.Probate;
                    if (labels.single) {
                      return (
                        <TextField
                          label={labels.prefix}
                          value={updatedName}
                          onChange={(e) => setUpdatedName(e.target.value)}
                          fullWidth
                          required
                          sx={{ mb: 3 }}
                        />
                      );
                    }
                    return (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TextField label={`${labels.prefix} First Name`} value={dwbFirstName} onChange={(e) => setDwbFirstName(e.target.value)} fullWidth required />
                          <TextField label={`${labels.prefix} Middle Name`} value={dwbMiddleName} onChange={(e) => setDwbMiddleName(e.target.value)} fullWidth />
                        </Box>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TextField label={`${labels.prefix} Last Name`} value={dwbLastName} onChange={(e) => setDwbLastName(e.target.value)} fullWidth required />
                          <TextField label="Suffix" value={dwbSuffix} onChange={(e) => setDwbSuffix(e.target.value)} sx={{ maxWidth: 120 }} />
                        </Box>
                        <TextField label={labels.alias} value={dwbAlias} onChange={(e) => setDwbAlias(e.target.value)} fullWidth />
                      </Box>
                    );
                  })()
                ) : (
                    <TextField
                      label="Updated Name"
                      name="updatedName"
                      value={updatedName}
                      onChange={(e) => setUpdatedName(e.target.value)}
                      fullWidth
                      required
                      sx={{ mb: 3 }}
                    />
                  )
                ) : (
                  <TextField
                    label="New Bond Amount (Rounded Up)"
                    name="newBondAmount"
                    value={newBondAmount}
                    onChange={(e) => setNewBondAmount(e.target.value)}
                    fullWidth
                    required
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                    }}
                    sx={{ mb: 3 }}
                  />
                )}

                {(selectedBondToEndorse.bond_type === "Probate" || selectedBondToEndorse.bond_type === "Guardianship") && (
                  <TextField
                    label="Updated Estimated Assets (optional)"
                    type="text"
                    inputMode="decimal"
                    value={estimatedAssets}
                    onChange={(e) => setEstimatedAssets(e.target.value)}
                    fullWidth
                    placeholder="e.g. 250000"
                    sx={{ mb: 2 }}
                    helperText="Fill the updated estimated assets value if additional assets were discovered."
                  />
                )}

                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Documents
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                  Acceptable Documentation:
                </Typography>
                <ul style={{ paddingLeft: "20px", marginBottom: "20px" }}>
                  {getEndorsementInstructions(selectedBondToEndorse).map(
                    (item, idx) => (
                      <li
                        key={idx}
                        style={{ fontSize: "0.9rem", marginBottom: "6px" }}
                      >
                        {item}
                      </li>
                    )
                  )}
                </ul>

                <Button
                  variant="outlined"
                  component="label"
                  disabled={isSubmittingEndorsement}
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Upload Supporting Document
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      const validTypes = [
                        "application/pdf",
                        "application/msword",
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                      ];
                      if (file && !validTypes.includes(file.type)) {
                        toast.error("Only PDF or Word documents are allowed.");
                        e.target.value = "";
                        return;
                      }
                      setEndorsementFile(file);
                      toast.success("File selected successfully!");
                    }}
                  />
                </Button>
                {endorsementFile && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Selected File: {endorsementFile.name}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Allowed file types: PDF, DOC, DOCX
                </Typography>

                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Contact Information
                </Typography>
                <TextField
                  label=" Name"
                  name="contactName"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  fullWidth
                  required
                  placeholder="Full name"
                  sx={{ mb: 2 }}
                />

                <TextField
                  label=" Email"
                  type="email"
                  name="contactEmail"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  fullWidth
                  required
                  placeholder="name@example.com"
                  sx={{ mb: 1 }}
                />
              </>
            ) : (
              <Typography variant="h6" sx={{ py: 5, textAlign: "center" }}>
                No bond selected.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEndorsementDialogOpen(false)}
              disabled={isSubmittingEndorsement}
            >
              Close
            </Button>
            {selectedBondToEndorse &&
              selectedBondToEndorse.bond_type !== "Blanket" && (
                <>
                  {(role === "internal_user" || role === "super_user") && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    disabled={!selectedBondToEndorse || isSubmittingEndorsement}
                    onClick={async () => {
                      if (!selectedBondToEndorse) return;
                      const getRiderUrl = () => {
                        switch (selectedBondToEndorse.bond_type) {
                          case "VA":
                            return `/release-rider-va/${selectedBondToEndorse.bond_id}`;
                          case "Trustee":
                            return `/release-rider-trustee/${selectedBondToEndorse.bond_id}`;
                          case "Probate":
                            return `/release-rider-probate/${selectedBondToEndorse.bond_id}`;
                          case "Blanket":
                            return `/release-rider-blanket/${selectedBondToEndorse.bond_id}`;
                          case "Guardianship":
                            return `/release-rider-guardianship/${selectedBondToEndorse.bond_id}`;
                          default:
                            return `/release-rider-guardianship/${selectedBondToEndorse.bond_id}`;
                        }
                      };
                      const isInternalOrSuper = role === "internal_user" || role === "super_user";
                      const countyOk = endorsementType !== "Name_Change_County" || !!endorsementToCounty;
                      const dwbOk = endorsementType !== "Name_Change_Deceased_Ward_Beneficiary" || (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single ? !!updatedName : !!(dwbFirstName || dwbLastName));
                      const nameChangeOk = endorsementType === "Name_Change_County" ? countyOk : (endorsementType === "Name_Change_Deceased_Ward_Beneficiary" ? dwbOk : (endorsementType === "Name_Change_Principal" ? !!updatedName : true));
                      const hasDetails = endorsementType &&
                        contactName &&
                        contactEmail &&
                        (isNameChangeEndorsement(endorsementType) ? nameChangeOk : (!!newBondAmount && !!endorsementFile));

                      if (isInternalOrSuper && hasDetails) {
                        setIsSubmittingEndorsement(true);
                        const formData = new FormData();
                        formData.append("endorsement_type", endorsementType);
                        if (isNameChangeEndorsement(endorsementType)) {
                          if (endorsementType === "Name_Change_County") {
                            formData.append("updated_name", (endorsementToCounty || "").trim().split(/\s+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" "));
                          } else if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
                            if (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single) {
                              formData.append("updated_name", updatedName);
                            } else {
                              formData.append("updated_name", [dwbFirstName, dwbMiddleName, dwbLastName, dwbSuffix].filter(Boolean).join(" "));
                              formData.append("updated_first_name", dwbFirstName);
                              formData.append("updated_middle_name", dwbMiddleName);
                              formData.append("updated_last_name", dwbLastName);
                              formData.append("updated_suffix", dwbSuffix);
                              formData.append("updated_alias", dwbAlias);
                            }
                          } else {
                            formData.append("updated_name", updatedName);
                          }
                        } else {
                          formData.append("updated_bond_amount", newBondAmount.replace(/[^\d.]/g, ""));
                        }
                        if (endorsementFile) formData.append("endorsement_file", endorsementFile);
                        formData.append("contact_name", contactName);
                        formData.append("contact_email", contactEmail);
                        const estAssetsNum = estimatedAssets !== "" && !Number.isNaN(Number(estimatedAssets.replace(/[^\d.]/g, ""))) ? Number(estimatedAssets.replace(/[^\d.]/g, "")) : null;
                        if ((selectedBondToEndorse.bond_type === "Probate" || selectedBondToEndorse.bond_type === "Guardianship") && estAssetsNum != null) {
                          formData.append("estimated_assets", estAssetsNum);
                        }
                        try {
                          await apiClient(
                            `/endorse-bond/`,
                            "POST",
                            formData,
                            { bond_id: selectedBondToEndorse.bond_id },
                            { "Content-Type": "multipart/form-data" }
                          );
                          toast.success("Bond endorsed successfully!");
                          setEndorsementDialogOpen(false);
                          await fetchInitialData();
                          navigate(getRiderUrl());
                        } catch (error) {
                          console.error("Endorsement failed:", error);
                          toast.error(error.message || "Failed to endorse the bond.");
                        } finally {
                          setIsSubmittingEndorsement(false);
                        }
                      } else {
                        const url = getRiderUrl();
                        if (url) navigate(url);
                      }
                    }}
                  >
                    Release Rider
                  </Button>
                  )}
                  {role !== "internal_user" && role !== "super_user" && (
                  <Button
                    variant="contained"
                    disabled={
                      isSubmittingEndorsement ||
                      ((!isNameChangeEndorsement(endorsementType) && !endorsementFile)) ||
                      (endorsementType === "Name_Change_County" && !endorsementToCounty) ||
                      (endorsementType === "Name_Change_Deceased_Ward_Beneficiary" &&
                        !(DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single ? updatedName : (dwbFirstName || dwbLastName)))
                    }
                    onClick={async () => {
                      const countyOk = endorsementType !== "Name_Change_County" || !!endorsementToCounty;
                      const dwbOk = endorsementType !== "Name_Change_Deceased_Ward_Beneficiary" || (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single ? !!updatedName : !!(dwbFirstName || dwbLastName));
                      const nameChangeOk = endorsementType === "Name_Change_County" ? countyOk : (endorsementType === "Name_Change_Deceased_Ward_Beneficiary" ? dwbOk : (endorsementType === "Name_Change_Principal" ? !!updatedName : true));
                      if (
                        !selectedBondToEndorse ||
                        (!isNameChangeEndorsement(endorsementType) && !endorsementFile) ||
                        (isNameChangeEndorsement(endorsementType) ? !nameChangeOk : !newBondAmount) ||
                    !endorsementType ||
                    !contactName ||
                    !contactEmail
                      ) {
                        toast.error(
                          isNameChangeEndorsement(endorsementType)
                            ? "Please fill all required fields and contact info."
                            : "Please fill all required fields, contact info, and upload the file."
                        );
                        return;
                      }

                      setIsSubmittingEndorsement(true);

                      const formData = new FormData();
                      formData.append("endorsement_type", endorsementType);
                      if (isNameChangeEndorsement(endorsementType)) {
                        if (endorsementType === "Name_Change_County") {
                          formData.append("updated_name", (endorsementToCounty || "").trim().split(/\s+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" "));
                        } else if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
                          if (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single) {
                            formData.append("updated_name", updatedName);
                          } else {
                            formData.append("updated_name", [dwbFirstName, dwbMiddleName, dwbLastName, dwbSuffix].filter(Boolean).join(" "));
                            formData.append("updated_first_name", dwbFirstName);
                            formData.append("updated_middle_name", dwbMiddleName);
                            formData.append("updated_last_name", dwbLastName);
                            formData.append("updated_suffix", dwbSuffix);
                            formData.append("updated_alias", dwbAlias);
                          }
                        } else {
                          formData.append("updated_name", updatedName);
                        }
                      } else {
                        formData.append(
                          "updated_bond_amount",
                          newBondAmount.replace(/[^\d.]/g, "")
                        );
                      }
                      if (endorsementFile) formData.append("endorsement_file", endorsementFile);
                      formData.append("contact_name", contactName);
                      formData.append("contact_email", contactEmail);
                      const estAssetsNum = estimatedAssets !== "" && !Number.isNaN(Number(estimatedAssets.replace(/[^\d.]/g, ""))) ? Number(estimatedAssets.replace(/[^\d.]/g, "")) : null;
                      if ((selectedBondToEndorse.bond_type === "Probate" || selectedBondToEndorse.bond_type === "Guardianship") && estAssetsNum != null) {
                        formData.append("estimated_assets", estAssetsNum);
                      }

                      try {
                        await apiClient(
                          `/endorse-bond/`,
                          "POST",
                          formData,
                          { bond_id: selectedBondToEndorse.bond_id },
                          {
                            "Content-Type": "multipart/form-data",
                          }
                        );

                        toast.success("Bond endorsed successfully!");
                        setEndorsementDialogOpen(false);
                        await fetchInitialData();
                      } catch (error) {
                        console.error("Endorsement failed:", error);
                        toast.error(
                          error.message || "Failed to endorse the bond."
                        );
                      } finally {
                        setIsSubmittingEndorsement(false);
                      }
                    }}
                  >
                    {isSubmittingEndorsement ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Submit Endorsement"
                    )}
                  </Button>
                  )}
                </>
              )}
          </DialogActions>
        </Dialog>

        {/* Mark Liberty Complete Confirmation Dialog */}
        <Dialog
          open={libertyConfirmDialogOpen}
          onClose={() => !isMarkingLibertyComplete && (setLibertyConfirmDialogOpen(false), setSelectedBondToLiberty(null))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Mark Liberty Complete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to mark this bond as Liberty complete? This action will prevent this bond from being released to liberty in the next run.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setLibertyConfirmDialogOpen(false);
                setSelectedBondToLiberty(null);
              }}
              color="primary"
              disabled={isMarkingLibertyComplete}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLibertyBypassConfirm}
              color="success"
              variant="contained"
              disabled={isMarkingLibertyComplete}
              startIcon={isMarkingLibertyComplete ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isMarkingLibertyComplete ? "Marking..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Liberty Bypass History Dialog */}
        <Dialog
          open={bypassHistoryDialogOpen}
          onClose={() => setBypassHistoryDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { maxHeight: "90vh" } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Liberty Bypass History</DialogTitle>
          <DialogContent dividers>
            <TextField
              size="small"
              placeholder="Search by bond #, type, estate, law firm, bypassed by..."
              value={bypassHistorySearch}
              onChange={(e) => setBypassHistorySearch(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            {bypassHistoryLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { backgroundColor: "#f5f5f5", fontWeight: 600 } }}>
                      <TableCell>Bond #</TableCell>
                      <TableCell>Bond Type</TableCell>
                      <TableCell>Sub Category</TableCell>
                      <TableCell>Estate</TableCell>
                      <TableCell>Law Firm</TableCell>
                      <TableCell>Bypassed By</TableCell>
                      <TableCell>Bypassed At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const q = (bypassHistorySearch || "").toLowerCase().trim();
                      const filtered = q
                        ? bypassHistoryList.filter((row) => {
                            const s = [
                              row.bond_number,
                              row.bond_type,
                              row.bond_sub_category,
                              row.estate_name,
                              row.lawfirm_name,
                              row.bypassed_by,
                              row.bypassed_at,
                            ]
                              .filter(Boolean)
                              .join(" ")
                              .toLowerCase();
                            return s.includes(q);
                          })
                        : bypassHistoryList;
                      return filtered.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>
                            {bypassHistoryList.length === 0 ? "No bypass history yet." : "No matches for your search."}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filtered.map((row, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>{row.bond_number ?? "—"}</TableCell>
                            <TableCell>{row.bond_type ?? "—"}</TableCell>
                            <TableCell>{row.bond_sub_category ?? "—"}</TableCell>
                            <TableCell>{row.estate_name ?? "—"}</TableCell>
                            <TableCell>{row.lawfirm_name ?? "—"}</TableCell>
                            <TableCell>{row.bypassed_by ?? "—"}</TableCell>
                            <TableCell>
                              {row.bypassed_at ? dayjs(row.bypassed_at).format("MMM D, YYYY h:mm A") : "—"}
                            </TableCell>
                          </TableRow>
                        ))
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBypassHistoryDialogOpen(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment Status Override Dialog */}
        <Dialog
          open={paymentOverrideDialogOpen}
          onClose={() => !isSubmittingPaymentOverride && (setPaymentOverrideDialogOpen(false), setSelectedBondForPayment(null))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Override Payment Status</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Override the payment status returned by Liberty for this bond.
              Use this only when you have verified the correct status outside of Liberty (e.g. Agency Bill reconciliation).
            </Typography>
            {selectedBondForPayment && (
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: "#f8f9fa", borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Bond</Typography>
                <Typography variant="body2">
                  <b>#{selectedBondForPayment.bond_number || "—"}</b>
                  {" "}- {selectedBondForPayment.bond_type} / {selectedBondForPayment.bond_sub_category || "N/A"}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Current: <b>{formatPaymentStatus(selectedBondForPayment.payment_status)}</b>
                  {selectedBondForPayment.payment_status_source === "manual_override" && " (manual)"}
                </Typography>
              </Box>
            )}
            <TextField
              select
              label="New Status"
              fullWidth
              value={paymentOverrideStatus}
              onChange={(e) => setPaymentOverrideStatus(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
              <MenuItem value="na">N/A (Not Applicable)</MenuItem>
            </TextField>
            <TextField
              label="Reason (optional)"
              fullWidth
              multiline
              rows={3}
              value={paymentOverrideReason}
              onChange={(e) => setPaymentOverrideReason(e.target.value)}
              placeholder="e.g. Agency Bill - paid by check ref #12345"
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPaymentOverrideDialogOpen(false);
                setSelectedBondForPayment(null);
              }}
              disabled={isSubmittingPaymentOverride}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePaymentOverrideConfirm}
              variant="contained"
              color="primary"
              disabled={isSubmittingPaymentOverride}
              startIcon={isSubmittingPaymentOverride ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {isSubmittingPaymentOverride ? "Saving..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment Override History Dialog */}
        <Dialog
          open={paymentOverrideHistoryOpen}
          onClose={() => setPaymentOverrideHistoryOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { maxHeight: "90vh" } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Payment Status Override History</DialogTitle>
          <DialogContent dividers>
            <TextField
              size="small"
              placeholder="Search by bond #, estate, law firm, overridden by, status..."
              value={paymentOverrideHistorySearch}
              onChange={(e) => setPaymentOverrideHistorySearch(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            {paymentOverrideHistoryLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 460 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { backgroundColor: "#f5f5f5", fontWeight: 600 } }}>
                      <TableCell>Bond #</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Estate</TableCell>
                      <TableCell>Law Firm</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>By</TableCell>
                      <TableCell>At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const q = (paymentOverrideHistorySearch || "").toLowerCase().trim();
                      const filtered = q
                        ? paymentOverrideHistoryList.filter((row) => {
                            const hay = [
                              row.bond_number,
                              row.bond_type,
                              row.estate_name,
                              row.lawfirm_name,
                              row.previous_status,
                              row.new_status,
                              row.overridden_by,
                              row.reason,
                            ]
                              .filter(Boolean)
                              .join(" ")
                              .toLowerCase();
                            return hay.includes(q);
                          })
                        : paymentOverrideHistoryList;
                      if (filtered.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              {paymentOverrideHistoryList.length === 0
                                ? "No overrides yet."
                                : "No matches for your search."}
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return filtered.map((row) => (
                        <TableRow key={row.override_id}>
                          <TableCell>{row.bond_number || "—"}</TableCell>
                          <TableCell>{row.bond_type || "—"}</TableCell>
                          <TableCell>{row.estate_name || "—"}</TableCell>
                          <TableCell>{row.lawfirm_name || "—"}</TableCell>
                          <TableCell>{formatPaymentStatus(row.previous_status)}</TableCell>
                          <TableCell>{formatPaymentStatus(row.new_status)}</TableCell>
                          <TableCell sx={{ maxWidth: 220, whiteSpace: "pre-wrap" }}>{row.reason || "—"}</TableCell>
                          <TableCell>{row.overridden_by || "—"}</TableCell>
                          <TableCell>
                            {row.overridden_at ? dayjs(row.overridden_at).format("MMM D, YYYY h:mm A") : "—"}
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPaymentOverrideHistoryOpen(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => !isDeleting && handleDeleteClose()}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Bond</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this bond? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleReviewBond} color="primary">
              Review Bond
            </Button>
            <Button onClick={handleDeleteClose} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={isDeleting}
              startIcon={isDeleting && <CircularProgress size={20} />}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default BondArchives;
